import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Grid,
  InputAdornment,
  Paper,
  TextField,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedAdvancedSearchFiltersStyles, sharedToolbarListStyles } from '@/__core/styles';
import { useModuleAccessibleCompanies, type ToolbarCompanyOption } from '@/__core/permissions/hooks/useModuleAccessibleCompanies';
import type { ModuleAccessibleCompaniesResult } from '@/__core/permissions/utils/resolveModuleAccessibleCompanies';

const toTitleCase = (value: string) => {
  const normalized = (value || '').trim();
  if (!normalized) return '';
  return normalized
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export type CompanyOption = { id: string; name: string };

export type ModuleCompanyScope = ModuleAccessibleCompaniesResult & {
  companyOptions: ToolbarCompanyOption[];
};

export type FilterOption = { value: string; label: string };

export type AdvancedFilterField = {
  key: string;
  label: string;
  value: string;
  placeholder?: string;
  labelWidth?: number;
  options: FilterOption[];
  onChange: (value: string) => void;
  gridSize?: { xs?: number; sm?: number; md?: number; lg?: number };
  render?: () => React.ReactNode;
};

export interface AppListToolbarGlobalProps {
  searchPlaceholder: string;
  searchQuery: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: () => void;
  isAdvancedSearchOpen: boolean;
  onToggleAdvancedSearch?: () => void;
  scopeModule: string;
  companies?: CompanyOption[];
  selectedCompany: string;
  onCompanyChange?: (companyId: string) => void;
  advancedFilterFields: AdvancedFilterField[];
  /** Custom advanced panel (e.g. Orders/Finance AdvancedSearchFilters) */
  advancedPanel?: React.ReactNode;
  /** Panel manages its own Collapse (e.g. finance AdvancedSearchFilters) */
  advancedPanelOwnCollapse?: boolean;
  /** Extra controls beside company autocomplete (e.g. column visibility) */
  rightSection?: React.ReactNode;
  rightActions?: React.ReactNode;
  /** When false, hides search and advanced-search toggle (e.g. insurance toolbar) */
  showSearch?: boolean;
  /** Custom left controls when search is hidden or before search field */
  leftSection?: React.ReactNode;
}

export default function AppListToolbarGlobal({
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  isAdvancedSearchOpen,
  onToggleAdvancedSearch,
  scopeModule,
  companies,
  selectedCompany,
  onCompanyChange,
  advancedFilterFields = [],
  advancedPanel,
  advancedPanelOwnCollapse = false,
  rightSection,
  rightActions,
  showSearch = true,
  leftSection,
}: AppListToolbarGlobalProps) {

  const resolvedCompanyScope = useModuleAccessibleCompanies(scopeModule);

  const companyScope = React.useMemo<ModuleCompanyScope>(() => {
    if (!companies?.length) {
      return resolvedCompanyScope;
    }
    const overrideOptions = companies
      .map((company) => ({ id: company.id, name: company.name }))
      .filter((company) => company.id && company.id !== 'global');

    return {
      ...resolvedCompanyScope,
      companyOptions: overrideOptions,
    };
  }, [companies, resolvedCompanyScope]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchSubmit?.();
    }
  };

  const companyOptions = React.useMemo(
    () => companyScope.companyOptions.filter((company) => company.id && company.id !== 'global'),
    [companyScope.companyOptions]
  );

  const normalizedSelectedCompany =
    !selectedCompany || selectedCompany === 'all' || selectedCompany === 'global' ? 'all' : selectedCompany;

  const selectedCompanyOption = React.useMemo(() => {
    if (normalizedSelectedCompany === 'all') {
      if (!companyScope.defaultCompanyId || companyScope.defaultCompanyId === 'global') return null;
      return companyOptions.find((c) => c.id === companyScope.defaultCompanyId) || null;
    }
    return companyOptions.find((c) => c.id === normalizedSelectedCompany) || null;
  }, [companyOptions, companyScope.defaultCompanyId, normalizedSelectedCompany]);

  const shouldShowCompanyAutocomplete = React.useMemo(() => {
    if (!companyScope.shouldShowAutocomplete) return false;
    return companyScope.hasGlobalRole || companyOptions.length > 1;
  }, [companyOptions.length, companyScope.hasGlobalRole, companyScope.shouldShowAutocomplete]);

  const handleCompanyChange = React.useCallback(
    (_: React.SyntheticEvent, value: { id: string; name: string } | null) => {
      const next = value?.id || 'all';
      if (next === (selectedCompany || 'all')) return;
      onCompanyChange?.(next);
    },
    [onCompanyChange, selectedCompany]
  );

  return (
    <Paper elevation={0} sx={sharedToolbarListStyles.containerPaper}>
      <Box sx={sharedToolbarListStyles.mainRow}>
        <Box sx={sharedToolbarListStyles.leftSection}>
          {leftSection}
          {showSearch && (
            <>
              <TextField
                size='small'
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={sharedToolbarListStyles.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon sx={{ fontSize: '1rem', color: '#6b7280' }} />
                    </InputAdornment>
                  ),
                }}
              />

              {onToggleAdvancedSearch && (
                <Button
                  variant='outlined'
                  startIcon={<FilterListIcon />}
                  onClick={onToggleAdvancedSearch}
                  sx={{
                    ...sharedToolbarListStyles.advancedSearchButton,
                    backgroundColor: isAdvancedSearchOpen ? '#f3f4f6' : 'transparent',
                  }}
                >
                  Tìm kiếm nâng cao{isAdvancedSearchOpen ? ' ↑' : ' ↓'}
                </Button>
              )}
            </>
          )}
        </Box>

        {(shouldShowCompanyAutocomplete || rightActions || rightSection) && (
          <Box sx={sharedToolbarListStyles.rightSection}>
            {rightSection}
            {rightActions}
            {shouldShowCompanyAutocomplete && (
              <Autocomplete
                size='small'
                options={companyOptions}
                value={selectedCompanyOption}
                onChange={handleCompanyChange}
                getOptionLabel={(option) => toTitleCase(option.name)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={sharedToolbarListStyles.companyAutocomplete}
                renderInput={(params) => <TextField {...params} placeholder='Chọn công ty' />}
              />
            )}
          </Box>
        )}
      </Box>

      {advancedPanelOwnCollapse && advancedPanel}

      {!advancedPanelOwnCollapse && (advancedPanel || advancedFilterFields.length > 0) && (
        <Collapse in={isAdvancedSearchOpen} timeout='auto' unmountOnExit>
          <Paper elevation={0} sx={sharedAdvancedSearchFiltersStyles.panelPaper}>
            {advancedPanel}
            {advancedFilterFields.length > 0 && (
              <Grid container columnSpacing={2} rowSpacing={0}>
                {advancedFilterFields.map((field) => (
                  <Grid key={field.key} size={field.gridSize || { xs: 12, sm: 6, md: 4 }}>
                    {field.render ? (
                      field.render()
                    ) : (
                      <LabeledField label={field.label} labelWidth={field.labelWidth || 80}>
                        <Autocomplete
                          size='small'
                          sx={{
                            ...sharedAdvancedSearchFiltersStyles.autocompleteField,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 0.5,
                              height: '30px',
                              fontSize: '0.875rem',
                              backgroundColor: 'white',
                              '& fieldset': {
                                borderColor: '#d1d5db',
                              },
                              '&:hover fieldset': {
                                borderColor: '#9ca3af',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#1976d2',
                              },
                            },
                            '& .MuiInputBase-input': {
                              padding: '6px 12px',
                            },
                          }}
                          options={field.options}
                          value={field.options.find((option) => option.value === field.value) || null}
                          onChange={(_, nextValue) => field.onChange(nextValue?.value || 'all')}
                          getOptionLabel={(option) => option.label}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              placeholder={field.placeholder || `Chọn ${field.label.toLowerCase()}`}
                            />
                          )}
                        />
                      </LabeledField>
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Collapse>
      )}
    </Paper>
  );
}

